/* eslint-disable @typescript-eslint/no-explicit-any */

import { Getter } from 'vuex-simple';
import ResponseError, { HttpServiceEvents } from '@/HostApp/services/HttpService/Error/ResponseError';

import { $bus } from '@/HostApp/services/Bus';

type Methods = 'GET' | 'HEAD' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
export class HttpServiceModule implements TRA.IBaseTransportService {
	public baseApiUrl = '';

	@Getter()
	public get headers(): TRA.IRequestHeaders {
		const { context } = window.aps;

		const headers: Array<Array<string>> = [
			['Content-Type', 'application/json'],
			['APS-Token', context._token],
			['bw_id', context._sessionId],
			['APS-Subscription-ID', context.subscriptionId],
			['APS-Locale', context._locale],
			['X-Correlation-ID', context._traceId],
			['X-UX1-Anonymous-URL-Path', context._anonymousUrlPath]
		].filter((item) => item[1]);

		return Object.fromEntries(headers);
	}

	// eslint-disable-next-line max-params
	private async doRequest<T>(
		method: Methods,
		url: string,
		config = {} as TRA.IRequestConfig,
		data?: any,
		options: TRA.IRequestOptions = {
			emitOnError: true
		}
	): Promise<TRA.IResponse<T>> {
		const requestHeaders = { ...this.headers, ...config.headers } as TRA.IRequestHeaders;
		const fetchOptions: RequestInit = {
			method,
			cache: 'no-cache',
			redirect: 'follow',
			referrerPolicy: 'origin',
			headers: requestHeaders
		};

		if (data) {
			fetchOptions.body = JSON.stringify(data);
		}

		const fullUrl = this.getUrlWithParams(url, config);

		const response: Response = await fetch(fullUrl, fetchOptions);

		const mappedResponse = await this.mapResponseToIResponse<T>(response, config, requestHeaders);

		if (response.ok) {
			return mappedResponse;
		} else {
			if (options.emitOnError) {
				$bus.$emit(HttpServiceEvents.ERROR, mappedResponse);
			}
			throw new ResponseError(
				mappedResponse,
				`Cannot ${method.toLowerCase()}: ${url}. Status code: ${response.status}.`
			);
		}
	}

	public async post<T>(url: string, data: any, config = {} as TRA.IRequestConfig): Promise<TRA.IResponse<T>> {
		return await this.doRequest<T>('POST', url, config, data);
	}

	public async put<T>(url: string, data: any, config = {} as TRA.IRequestConfig): Promise<TRA.IResponse<T>> {
		return await this.doRequest<T>('PUT', url, config, data);
	}

	public async patch<T>(url: string, data: any, config = {} as TRA.IRequestConfig): Promise<TRA.IResponse<T>> {
		return await this.doRequest<T>('PATCH', url, config, data);
	}

	public async get<T>(
		url: string,
		config = {} as TRA.IRequestConfig,
		options = {} as TRA.IRequestOptions
	): Promise<TRA.IResponse<T>> {
		return await this.doRequest<T>('GET', url, config, null, options);
	}

	public async delete<T>(url: string, data: any, config = {} as TRA.IRequestConfig): Promise<TRA.IResponse<T>> {
		return await this.doRequest<T>('DELETE', url, config, data);
	}

	public async head<T>(url: string, config = {} as TRA.IRequestConfig): Promise<TRA.IResponse<T>> {
		return await this.doRequest<T>('HEAD', url, config);
	}

	private async mapResponseToIResponse<T>(
		response: Response,
		config: TRA.IRequestConfig,
		requestHeaders: TRA.IRequestHeaders
	): Promise<TRA.IResponse<T>> {
		let data = {} as T;
		try {
			const handleAs = config.handleAs || 'json';

			switch (handleAs) {
				case 'text':
					data = (await response.text()) as T;
					break;
				case 'json':
					data = await response.json();
					break;
			}
		} catch {
			data = {} as T;
		}

		return {
			data,
			body: response.body,
			status: response.status,
			isError: !response.ok,
			request: new LastRequest(requestHeaders, response, config),
			config
		};
	}

	private getUrlWithParams(url: string, config: { params?: Common.IMap<string> }): string {
		const params =
			config.params && Object.keys(config.params).length > 0
				? `?${new URLSearchParams(this.cleanParams(config.params))}`
				: '';

		return `${url}${params}`;
	}

	/**
	 * This method replaces all undefined values from an object with an empty string
	 * @param params
	 * @private
	 */
	private cleanParams(params: Common.IMap<string>): Common.IMap<string> {
		return Object.fromEntries(Object.keys(params).map((key) => [key, params[key] ?? '']));
	}
}

class LastRequest implements TRA.ILastRequest {
	public options: TRA.ILastRequestOptions;
	public response: TRA.ILastRequestResponse;
	constructor(requestHeaders: TRA.IRequestHeaders, response: Response, config: TRA.IRequestConfig) {
		this.options = {
			handleAs: config.handleAs || 'json',
			headers: Object.fromEntries(response.headers.entries())
		};

		this.response = {
			headers: requestHeaders
		};
	}

	public getHeader(key: string): string {
		return (this.options.headers[key.toLowerCase() as keyof TRA.IRequestHeaders] || '') as string;
	}
}

const httpServiceModule = new HttpServiceModule();

export default httpServiceModule;
